import { DataTypeKey, FieldMapping, MatchKey, SourceSystem } from '../types';

export interface SourceSampleRow {
  externalId: string;
  label: string;
  secondary: string;
}

/** What one system type offers for one data type. */
export interface SystemDataTypeFixture {
  dataType: DataTypeKey;
  sourceFields: string[];
  defaultMapping: FieldMapping[];
  defaultMatchKey?: MatchKey;
  sampleRows: SourceSampleRow[];
  newCount: number;
  updatedCount: number;
}

export interface SystemFixture {
  system: SourceSystem;
  defaultTenantUrl: string;
  scopes: string[];
  /** Data types this system exposes at all. Anything absent renders as Not offered. */
  offers: SystemDataTypeFixture[];
}

const SAP_EMPLOYEES: SystemDataTypeFixture = {
  dataType: 'employees',
  sourceFields: [
    'personalInfo.firstName',
    'personalInfo.lastName',
    'personalInfo.workEmail',
    'employmentInfo.employeeId',
    'employmentInfo.startDate',
    'employmentInfo.employmentType',
    'employmentInfo.managerId',
    'employmentInfo.departmentId',
    'cust_engagementOptIn',
  ],
  defaultMapping: [
    { sourceField: 'personalInfo.firstName', targetField: 'firstName', targetLabelKey: 'extInt.field.firstName', required: true },
    { sourceField: 'personalInfo.lastName', targetField: 'lastName', targetLabelKey: 'extInt.field.lastName', required: true },
    { sourceField: 'personalInfo.workEmail', targetField: 'workEmail', targetLabelKey: 'extInt.field.workEmail', required: true },
    { sourceField: 'employmentInfo.employeeId', targetField: 'employeeNumber', targetLabelKey: 'extInt.field.employeeNumber', required: false },
    { sourceField: 'employmentInfo.startDate', sourceFormatHint: 'dd/MM/yyyy', targetField: 'hireDate', targetLabelKey: 'extInt.field.hireDate', required: false },
    {
      sourceField: 'employmentInfo.employmentType',
      targetField: 'employmentType',
      targetLabelKey: 'extInt.field.employmentType',
      required: false,
      valueMap: [
        { sourceValue: 'FT', targetValue: 'FullTime' },
        { sourceValue: 'PT', targetValue: 'PartTime' },
        { sourceValue: 'CN', targetValue: null },
      ],
    },
    { sourceField: 'employmentInfo.departmentId', targetField: 'departmentId', targetLabelKey: 'extInt.field.department', required: false },
    { sourceField: 'employmentInfo.managerId', targetField: 'managerId', targetLabelKey: 'extInt.field.manager', required: false },
  ],
  defaultMatchKey: 'workEmail',
  sampleRows: [
    { externalId: 'EMP-1042', label: 'Layla Haddad', secondary: 'Operations' },
    { externalId: 'EMP-1188', label: 'Omar Hassan', secondary: 'Field Services' },
    { externalId: 'EMP-1203', label: 'Sara Nasser', secondary: 'People Team' },
  ],
  newCount: 238,
  updatedCount: 3,
};

const SAP_DEPARTMENTS: SystemDataTypeFixture = {
  dataType: 'departments',
  sourceFields: ['foDepartment.externalCode', 'foDepartment.name', 'foDepartment.parentId', 'foDepartment.costCenter'],
  defaultMapping: [
    { sourceField: 'foDepartment.name', targetField: 'name', targetLabelKey: 'extInt.field.name', required: true },
    { sourceField: 'foDepartment.parentId', targetField: 'parentId', targetLabelKey: 'extInt.field.parent', required: false },
  ],
  sampleRows: [
    { externalId: 'DEP-OPS', label: 'Operations', secondary: 'Parent: none' },
    { externalId: 'DEP-ENG', label: 'Engineering', secondary: 'Parent: none' },
  ],
  newCount: 19,
  updatedCount: 0,
};

const SAP_JOB_ROLES: SystemDataTypeFixture = {
  dataType: 'jobRoles',
  sourceFields: ['jobCode.externalCode', 'jobCode.name', 'jobCode.description', 'jobCode.jobFamily', 'jobCode.grade'],
  defaultMapping: [
    { sourceField: 'jobCode.name', targetField: 'title', targetLabelKey: 'extInt.field.roleTitle', required: true },
    { sourceField: 'jobCode.description', targetField: 'description', targetLabelKey: 'extInt.field.roleDescription', required: false },
    { sourceField: 'jobCode.jobFamily', targetField: 'family', targetLabelKey: 'extInt.field.jobFamily', required: false },
  ],
  sampleRows: [
    { externalId: 'JOB-114', label: 'Field Technician', secondary: 'Description present' },
    { externalId: 'JOB-161', label: 'Finance Analyst', secondary: 'No description' },
  ],
  newCount: 34,
  updatedCount: 2,
};

const SAP_GOALS: SystemDataTypeFixture = {
  dataType: 'goals',
  sourceFields: ['goal.id', 'goal.name', 'goal.metric', 'goal.dueDate', 'goal.state', 'goal.ownerId'],
  defaultMapping: [
    { sourceField: 'goal.name', targetField: 'title', targetLabelKey: 'extInt.field.goalTitle', required: true },
    { sourceField: 'goal.metric', targetField: 'measure', targetLabelKey: 'extInt.field.goalMeasure', required: false },
    { sourceField: 'goal.dueDate', sourceFormatHint: 'dd/MM/yyyy', targetField: 'dueDate', targetLabelKey: 'extInt.field.dueDate', required: false },
    { sourceField: 'goal.ownerId', targetField: 'ownerId', targetLabelKey: 'extInt.field.owner', required: true },
  ],
  sampleRows: [
    { externalId: 'GOAL-8801', label: 'Reduce vehicle downtime', secondary: 'Due 31/12/2026' },
    { externalId: 'GOAL-8907', label: 'Close month end faster', secondary: 'No measure' },
  ],
  newCount: 412,
  updatedCount: 0,
};

const SAP_SKILLS: SystemDataTypeFixture = {
  dataType: 'skills',
  sourceFields: ['competency.id', 'competency.name', 'competency.rating', 'competency.userId', 'competency.category'],
  defaultMapping: [
    { sourceField: 'competency.name', targetField: 'skillName', targetLabelKey: 'extInt.field.skillName', required: true },
    { sourceField: 'competency.userId', targetField: 'userId', targetLabelKey: 'extInt.field.employee', required: true },
    {
      sourceField: 'competency.rating',
      targetField: 'proficiency',
      targetLabelKey: 'extInt.field.proficiency',
      required: false,
      valueMap: [
        { sourceValue: '1', targetValue: 'Beginner' },
        { sourceValue: '2', targetValue: 'Beginner' },
        { sourceValue: '3', targetValue: 'Intermediate' },
        { sourceValue: '4', targetValue: 'Advanced' },
        { sourceValue: '5', targetValue: 'Expert' },
      ],
    },
  ],
  sampleRows: [
    { externalId: 'CMP-3301', label: 'Hydraulic systems', secondary: 'Rating 4 of 5' },
    { externalId: 'CMP-3388', label: 'Financial modelling', secondary: 'Rating 5 of 5' },
  ],
  newCount: 1109,
  updatedCount: 0,
};

// Oracle HCM deliberately names the same fields differently, which is what makes repointing a data
// type at another source reset its mapping rather than carry the old names across.
const ORACLE_EMPLOYEES: SystemDataTypeFixture = {
  dataType: 'employees',
  sourceFields: [
    'PersonName.FirstName',
    'PersonName.LastName',
    'PersonEmail.EmailAddress',
    'Worker.PersonNumber',
    'WorkRelationship.LegalEmployerHireDate',
    'Assignment.AssignmentType',
    'Assignment.DepartmentId',
  ],
  defaultMapping: [
    { sourceField: 'PersonName.FirstName', targetField: 'firstName', targetLabelKey: 'extInt.field.firstName', required: true },
    { sourceField: 'PersonName.LastName', targetField: 'lastName', targetLabelKey: 'extInt.field.lastName', required: true },
    { sourceField: 'PersonEmail.EmailAddress', targetField: 'workEmail', targetLabelKey: 'extInt.field.workEmail', required: true },
    { sourceField: 'Worker.PersonNumber', targetField: 'employeeNumber', targetLabelKey: 'extInt.field.employeeNumber', required: false },
    { sourceField: 'WorkRelationship.LegalEmployerHireDate', sourceFormatHint: 'yyyy-MM-dd', targetField: 'hireDate', targetLabelKey: 'extInt.field.hireDate', required: false },
    { sourceField: 'Assignment.DepartmentId', targetField: 'departmentId', targetLabelKey: 'extInt.field.department', required: false },
    { sourceField: null, targetField: 'managerId', targetLabelKey: 'extInt.field.manager', required: false },
  ],
  defaultMatchKey: 'employeeNumber',
  sampleRows: [
    { externalId: 'PER-70114', label: 'Layla Haddad', secondary: 'Operations' },
    { externalId: 'PER-70255', label: 'Omar Hassan', secondary: 'Field Services' },
  ],
  newCount: 240,
  updatedCount: 6,
};

const ORACLE_DEPARTMENTS: SystemDataTypeFixture = {
  dataType: 'departments',
  sourceFields: ['Department.DepartmentId', 'Department.Name', 'Department.TreeCode'],
  defaultMapping: [
    { sourceField: 'Department.Name', targetField: 'name', targetLabelKey: 'extInt.field.name', required: true },
    { sourceField: 'Department.TreeCode', targetField: 'parentId', targetLabelKey: 'extInt.field.parent', required: false },
  ],
  sampleRows: [
    { externalId: 'ORG-1001', label: 'Operations', secondary: 'Parent: none' },
  ],
  newCount: 19,
  updatedCount: 0,
};

export const SYSTEM_FIXTURES: SystemFixture[] = [
  {
    system: 'sapSuccessFactors',
    // Shaped like a real SuccessFactors API host so the field doubles as a format hint.
    defaultTenantUrl: 'https://api4.successfactors.com',
    scopes: ['UserRead', 'EmpJobRead', 'GoalRead', 'CompetencyRead'],
    // No assets: SAP SuccessFactors carries no equipment-issuance record.
    offers: [SAP_EMPLOYEES, SAP_DEPARTMENTS, SAP_JOB_ROLES, SAP_GOALS, SAP_SKILLS],
  },
  {
    system: 'oracleHcm',
    defaultTenantUrl: 'https://acme.fa.em2.oraclecloud.com',
    scopes: ['WorkerRead', 'DepartmentRead'],
    offers: [ORACLE_EMPLOYEES, ORACLE_DEPARTMENTS],
  },
];

export function getSystemFixture(system: SourceSystem): SystemFixture | undefined {
  return SYSTEM_FIXTURES.find((s) => s.system === system);
}

/** What a given system offers for a given data type, or undefined if it does not offer it at all. */
export function getOffer(system: SourceSystem, dataType: DataTypeKey): SystemDataTypeFixture | undefined {
  return getSystemFixture(system)?.offers.find((o) => o.dataType === dataType);
}

/** The advisory output shown when the assets data type has no source anywhere. */
export const IMPLIED_EQUIPMENT_PROFILE = [
  { itemKey: 'extInt.equipment.laptop', quantity: 18 },
  { itemKey: 'extInt.equipment.fieldRadio', quantity: 12 },
  { itemKey: 'extInt.equipment.safetyKit', quantity: 6 },
  { itemKey: 'extInt.equipment.vehicle', quantity: 4 },
];
